import SuperButton from '@app/components/super-button';
import { currencyUnit, formatCurrency } from '@app/components/price/currency-formatter';
import {
  BranchItemFragment,
  OnlineOrderBranchConfigurationItemFragment,
  OnlineOrderItemFragment,
} from '@app/graphql/types/graphql';
import getImageLink from '@app/utils/get-image-link';
import { useTranslate } from '@tolgee/react';
import { useState } from 'react';
import useSubmitManualTransferProof from './use-submit-manual-transfer-proof';
import { uploadImage } from './use-upload-image';

interface Props {
  onlineOrder: OnlineOrderItemFragment;
  branch: BranchItemFragment;
  configuration: OnlineOrderBranchConfigurationItemFragment;
}

const MIN_SENDER_NAME_LENGTH = 2;

export default function ManualBankTransferPage({ onlineOrder, branch, configuration }: Props) {
  const currency = branch.company.settings.currency;
  const { t } = useTranslate();
  const [submitProof, { loading: submitting }] = useSubmitManualTransferProof();

  const methods = configuration.onlinePaymentMethods ?? [];
  const [activeMethodId, setActiveMethodId] = useState<string | null>(
    methods.length > 0 ? methods[0].id : null,
  );
  const activeMethod = methods.find((m) => m.id === activeMethodId) ?? methods[0] ?? null;

  const [senderName, setSenderName] = useState(onlineOrder.manualTransferSenderName ?? '');
  const [uploadedImage, setUploadedImage] = useState<{ origin: string; thumbnail: string } | null>(
    onlineOrder.manualTransferProofImageOrigin && onlineOrder.manualTransferProofImageThumbnail
      ? {
          origin: onlineOrder.manualTransferProofImageOrigin,
          thumbnail: onlineOrder.manualTransferProofImageThumbnail,
        }
      : null,
  );
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // This page only renders when no proof has been submitted yet (the parent router switches
  // to OrderDetailPage once manualTransferProofSubmittedAt is set), so any rejection field
  // present here means the previous proof was rejected and the customer needs to re-upload.
  const wasRejected = Boolean(onlineOrder.manualTransferRejectedAt);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(null);
    setUploading(true);
    try {
      const result = await uploadImage(onlineOrder.id, file);
      setUploadedImage({ origin: result.originPath, thumbnail: result.thumbnailPath });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleSubmit = async () => {
    if (!uploadedImage) return;
    if (senderName.trim().length < MIN_SENDER_NAME_LENGTH) {
      setError(
        t(
          'online-order.online-payment.proof.error.sender-name',
          'Please enter the name on the bank account you used (at least 2 characters)',
        ),
      );
      return;
    }
    setError(null);
    try {
      await submitProof({
        variables: {
          onlineOrderID: onlineOrder.id,
          input: {
            imageOriginPath: uploadedImage.origin,
            imageThumbnailPath: uploadedImage.thumbnail,
            senderName: senderName.trim(),
          },
        },
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Submission failed');
    }
  };

  const copy = (value: string) => {
    void navigator.clipboard.writeText(value);
  };

  const submitDisabled =
    submitting || uploading || !uploadedImage || senderName.trim().length < MIN_SENDER_NAME_LENGTH;

  return (
    <div className="manual-bank-transfer flex min-h-screen flex-col items-center justify-center p-4">
      <div className="w-full max-w-lg space-y-6">
        <div>
          <h1 className="text-2xl font-bold mb-2">
            {t('online-order.online-payment.page.title', 'Pay online')}
          </h1>
          <p className="text-gray-700 text-sm">
            {t(
              'online-order.online-payment.page.description',
              'Choose a payment method below, complete the transfer, then upload your payment screenshot. The amount you owe is shown at the bottom.',
            )}
          </p>
        </div>

        {wasRejected && onlineOrder.manualTransferRejectionReason && (
          <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-800">
            {t(
              'online-order.online-payment.page.rejected',
              'Your last proof was rejected: {reason}. Please upload a new screenshot.',
              { reason: onlineOrder.manualTransferRejectionReason },
            )}
          </div>
        )}

        {methods.length === 0 ? (
          <div className="rounded-md bg-yellow-50 border border-yellow-200 p-4 text-sm text-yellow-800">
            {t(
              'online-order.online-payment.page.no-methods',
              'This restaurant has not configured any online payment methods yet. Please contact them directly.',
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {methods.length > 1 && (
              <div className="flex flex-wrap gap-2">
                {methods.map((method) => (
                  <button
                    key={method.id}
                    type="button"
                    onClick={() => setActiveMethodId(method.id)}
                    className={
                      'px-3 py-1.5 text-sm font-medium rounded-full border ' +
                      (activeMethod?.id === method.id
                        ? 'bg-indigo-600 border-indigo-600 text-white'
                        : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50')
                    }
                  >
                    {method.name}
                  </button>
                ))}
              </div>
            )}

            {activeMethod && (
              <div className="rounded-lg border border-gray-200 bg-white p-4 space-y-3">
                <h2 className="font-semibold">{activeMethod.name}</h2>

                {activeMethod.qrImageOrigin && (
                  <div className="flex justify-center">
                    <img
                      src={getImageLink(activeMethod.qrImageOrigin)}
                      alt={`${activeMethod.name} QR code`}
                      className="max-w-xs w-full rounded-lg border border-gray-100"
                    />
                  </div>
                )}

                <div className="space-y-2 text-sm">
                  {activeMethod.fields.map((field, idx) => (
                    <InfoRow
                      key={idx}
                      label={field.key}
                      value={field.value}
                      onCopy={() => copy(field.value)}
                    />
                  ))}
                  <InfoRow
                    label={t('online-order.online-payment.page.amount', 'Amount')}
                    value={
                      onlineOrder.totalPrice != null
                        ? formatCurrency(onlineOrder.totalPrice, currency)
                        : '—'
                    }
                    onCopy={
                      onlineOrder.totalPrice != null
                        ? () => copy(String(onlineOrder.totalPrice! / currencyUnit(currency)))
                        : undefined
                    }
                  />
                </div>

                {activeMethod.instructions && (
                  <p className="text-sm text-gray-700 whitespace-pre-wrap border-t border-gray-100 pt-3">
                    {activeMethod.instructions}
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        <div className="rounded-lg border border-gray-200 bg-white p-4 space-y-4">
          <h2 className="font-semibold">
            {t('online-order.online-payment.page.submit-proof', 'Submit your payment proof')}
          </h2>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('online-order.online-payment.page.sender-name', 'Sender name (account holder)')}
            </label>
            <input
              type="text"
              value={senderName}
              onChange={(e) => setSenderName(e.target.value)}
              placeholder={t(
                'online-order.online-payment.page.sender-name.placeholder',
                'Your name on the bank account',
              )}
              className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('online-order.online-payment.page.screenshot', 'Payment screenshot')}
            </label>
            {uploadedImage ? (
              <div className="space-y-2">
                <img
                  src={getImageLink(uploadedImage.thumbnail)}
                  alt="Proof preview"
                  className="max-h-48 max-w-xs object-contain rounded-lg border border-gray-200"
                />
                <button
                  type="button"
                  className="text-sm text-red-600 hover:underline"
                  onClick={() => setUploadedImage(null)}
                >
                  {t('online-order.online-payment.page.screenshot.remove', 'Remove')}
                </button>
              </div>
            ) : (
              <input
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                disabled={uploading}
                className="text-sm"
              />
            )}
            {uploading && <p className="text-sm text-gray-500 mt-1">Uploading...</p>}
          </div>

          {error && (
            <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-800">{error}</div>
          )}

          <SuperButton onClick={handleSubmit} disabled={submitDisabled} className="w-full">
            {submitting
              ? t('common.button.loading', 'Loading...')
              : t('online-order.online-payment.page.submit', 'Submit proof')}
          </SuperButton>
        </div>
      </div>
    </div>
  );
}

function InfoRow(props: { label: string; value: string; onCopy?: () => void }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="flex flex-col">
        <span className="text-gray-500">{props.label}</span>
        <span className="font-medium text-gray-900 break-all">{props.value}</span>
      </div>
      {props.onCopy && (
        <button
          type="button"
          onClick={props.onCopy}
          className="shrink-0 rounded-md border border-gray-300 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50"
        >
          Copy
        </button>
      )}
    </div>
  );
}
